import { LocalStorage, redirect } from '@libs';
import { useTranslations } from '@libs/translations';
import { auth } from '@modules/auth';
import { POP_CURRENT_TEAM } from '@constants';
import {
  NOT_TOKENS_FOR_REQUEST,
  ON_AUTH_REQUEST_ERROR_400,
  ON_CABINET,
  ON_LOGOUT,
  ON_OPEN_GALLERY,
  ON_OPEN_IMAGE_EDITOR,
  ON_OPEN_STORE_CART,
  ON_OPEN_STORE,
  ON_SUCCESS_LOGIN,
  ON_SUCCESS_SIGNUP,
  ON_ROUTE,
  ON_OPEN_ESTIMATE,
} from '@constants/observer';
import { DEFAULT_LOCALE, PRIMARY_LANG } from '@constants/langs';

let instance = null;

class Ui {
  constructor() {
    if (!instance) {
      instance = this;
    }

    this.observer = null;
    this.mode = 'site';
    this.userWidget = null;
    this.store = null;
    this.gallery = null;
    this.imageEditor = null;
    this.formEntry = null;
    this.notifications = null;
    this.estimate = null;

    return instance;
  }

  get teamId() {
    return LocalStorage.get(POP_CURRENT_TEAM) || '';
  }

  _checkLoad(key) {
    const maxCount = 20;
    const interval = 200;
    let count = 0;
    let timeout = '';

    return new Promise((resolve) => {
      const checkReady = () => {
        count++;

        if (this[key]) {
          resolve();
          clearTimeout(timeout);
        } else if (count < maxCount) {
          timeout = setTimeout(() => checkReady(), interval);
        }
      };

      checkReady();
    });
  }

  initObserver({ locale }) {
    this.observer = new window.POPObserver();

    this.observer.addEventListener(ON_ROUTE, (path) => {
      redirect(path, {
        locale: path === '/' && locale === PRIMARY_LANG.value ? DEFAULT_LOCALE : locale,
      });
    });
  }

  addEventListener(key, callback) {
    this._checkLoad('observer').then(() => this.observer.addEventListener(key, callback));
  }

  removeEventListener(key) {
    if (this.observer) {
      this.observer.removeEventListener(key);
    }
  }

  postMessage(key, value) {
    if (this.observer) {
      this.observer.postMessage(key, value);
    }
  }

  connectTranslations() {
    const translations = useTranslations();

    if (window.POPConnectTranslations) {
      window.POPConnectTranslations(translations.translation);
    }
  }

  initUserWidget({ locale }) {
    if (!window.POPUserWidget) {
      return;
    }

    this.userWidget = new window.POPUserWidget();

    this.userWidget.init({
      root: 'user-widget',
      mode: this.mode,
      locale,
    });
  }

  openUserWidget() {
    this._checkLoad('userWidget').then(() => this.userWidget.open());
  }

  closeUserWidget() {
    if (this.userWidget) {
      this.userWidget.close();
    }
  }

  reinitUserWidget({ locale, isUser }) {
    if (isUser) {
      this.closeUserWidget();
      this.initUserWidget({ locale });
      this.openUserWidget();
    }
  }

  initGallery({ locale }) {
    if (!window.UIGallery) {
      return;
    }

    this.gallery = new window.UIGallery();

    this.gallery.init({
      root: 'gallery',
      mode: this.mode,
      locale,
    });
  }

  initGalleryListeners() {
    this.observer.addEventListener(ON_OPEN_GALLERY, (payload) => {
      const { projectId, openImageId, previewUrl, imageUrl } = payload;

      this.gallery.open({
        projectId,
        openImageId,
        previewUrl,
        imageUrl,
        team_id: this.teamId,
      });
    });
  }

  openPreviewGallery(src = '', sharedUser = null, params) {
    const { isPanorama = false, isTour = false, isEstimate = false, estimateData = {} } = params;
    const isKrpano = isPanorama || isTour;

    this._checkLoad('gallery').then(() =>
      this.gallery.open({
        imageUrl: !isKrpano && src,
        panoramaUrl: isPanorama && src,
        tourUrl: isTour && src,
        sharedUser,
        team_id: this.teamId,
        isPreviewMode: true,
        useBodyScrollLock: false,
        isEstimate,
        estimateData,
      })
    );
  }

  closeGallery() {
    if (this.gallery) {
      this.gallery.close();
    }
  }

  reinitGallery({ locale }) {
    this.initGallery({ locale });
  }

  initImageEditor({ locale }) {
    if (!window.UIImageEditor) {
      return;
    }

    this.imageEditor = new window.UIImageEditor();

    this.imageEditor.init({
      root: 'image-editor',
      mode: this.mode,
      locale,
    });
  }

  initOpenImageEditor() {
    this.observer.addEventListener(ON_OPEN_IMAGE_EDITOR, (payload) => {
      this.imageEditor.open(payload);
    });
  }

  initEstimateListeners({ locale }) {
    if (!window.UIEstimate) {
      return;
    }

    this.estimate = new window.UIEstimate();

    this.observer.addEventListener(ON_OPEN_ESTIMATE, (payload) => {
      const { root, data } = payload;

      this.estimate.init({
        root,
        locale,
        isPreview: true,
        data,
      });
      this.estimate.open();
    });
  }

  initStore({ locale }) {
    if (!window.UIStore) {
      return;
    }

    this.store = new window.UIStore();

    this.store.init({
      root: 'store',
      mode: this.mode,
      locale,
    });
  }

  initStoreListeners() {
    this.observer.addEventListener(ON_OPEN_STORE, (params) => {
      this.store.open({
        initialScreen: 'pricing',
        team_id: this.teamId,
        ...params
      });
    });

    this.observer.addEventListener(ON_OPEN_STORE_CART, () => {
      this.store.open({
        initialScreen: 'cart',
        team_id: this.teamId,
      });
    });
  }

  openStore({ tariff = ''}) {
    const options = {
      team_id: this.teamId,
    };

    if (tariff) {
      options.cartTariff = tariff;
    }

    this.store.open(options);
  }

  reinitStore({ locale }) {
    this.initStore({ locale });
  }

  initFormEntry({ locale }) {
    if (!window.POPFormEntry) {
      return;
    }

    this.formEntry = new window.POPFormEntry();

    this.formEntry.init({
      root: 'form-entry',
      mode: this.mode,
      locale,
    });
  }

  initFormEntryListeners({ locale }) {
    // this.observer.addEventListener(ON_RECEIVED_TOKENS, ({ id }) => auth.setUser({ id }));
    this.observer.addEventListener(ON_LOGOUT, () => auth.logout({ locale }));
    this.observer.addEventListener(NOT_TOKENS_FOR_REQUEST, () => auth.logout({ locale }));
    this.observer.addEventListener(ON_AUTH_REQUEST_ERROR_400, () => auth.logout({ locale }));
    this.observer.addEventListener(ON_CABINET, () => (window.location = `/${locale}/cabinet/`));
  }

  openFormEntry(step = 'login', onCloseModal, redirectUrl = '/', { locale, isAfterOpenProject }) {
    this._checkLoad('formEntry').then(() => this.formEntry.open({ initialStep: step, isAfterOpenProject }));

    const onAfterLogin = () => {
      if (isAfterOpenProject) {
        onCloseModal();
        auth.setUser();

        return;
      }

      if (redirectUrl) {
        window.location = `/${locale}${redirectUrl}`;
      } else {
        onCloseModal();
        auth.setUser();
      }
    };

    this.addEventListener(ON_SUCCESS_LOGIN, onAfterLogin);
    this.addEventListener(ON_SUCCESS_SIGNUP, onCloseModal);
  }

  getGoogleCode({ method }) {
    if (!this.formEntry) {
      return;
    }

    this.formEntry.getGoogleCode({ method });
  }

  getVkCode({ method }) {
    if (!this.formEntry) {
      return;
    }

    this.formEntry.getVkCode({ method });
  }

  getFacebookCode({ method }) {
    if (!this.formEntry) {
      return;
    }
    this.formEntry.getFacebookCode({ method });
  }

  closeFormEntry() {
    if (this.formEntry) {
      this.formEntry.close();
      this.observer.removeEventListener(ON_SUCCESS_LOGIN);
      this.observer.removeEventListener(ON_SUCCESS_SIGNUP);
    }
  }

  reinitFormEntry({ locale }) {
    this.initFormEntry({ locale });
  }

  initNotifications() {
    if (!window.POPNotifications) {
      return;
    }

    this.notifications = new window.POPNotifications();
    this.notifications.init('notifications');
  }

  showSuccessNotification(message = '') {
    if (this.notifications) {
      this.notifications.showSuccess(message);
    }
  }

  showErrorNotification(message = '') {
    if (this.notifications) {
      this.notifications.showError(message);
    }
  }
}

export const ui = new Ui();
